import * as v from 'valibot'
import { CONTRACT_FIELDS, LOGIC_OPTIONS, onWorksheet } from './contract'
import {
  CHANGE_TYPES,
  approvals,
  latestChallenge,
  returning,
  workType,
} from './decisions'
import type { PolicyDecision } from './decisions'
import { coverage } from './claims'
import { unsettledReason } from './gates'
import { POLICY_ROLES, POLICY_STAGES } from './types'
import type { PolicyDocView, PolicyRole, PolicyStage } from './types'
import { definitionFingerprint } from './versions'

/* Wire vocabulary for one policy room and its registry. No variant carries an
   actor: attribution always comes from the verified connection or request,
   never from a payload a client wrote. */

const text = v.pipe(v.string(), v.trim(), v.minLength(1))
const stage = v.picklist(POLICY_STAGES)
const answerValue = v.union([
  v.string(),
  v.array(v.string()),
  v.object({ choice: v.string(), other: v.optional(v.string()) }),
])
const note = v.pipe(v.string(), v.trim(), v.maxLength(2000))
const noteBody = v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(4000))

/** A sha256 hex digest: how a file is identified across Nucleus, R2 and Source rooms. */
export const fileHashSchema = v.pipe(v.string(), v.regex(/^[0-9a-f]{64}$/))

export const pdfAnchorSchema = v.object({
  documentHash: fileHashSchema,
  pageIndex: v.pipe(v.number(), v.integer(), v.minValue(0)),
  rects: v.array(
    v.object({
      x: v.number(),
      y: v.number(),
      width: v.number(),
      height: v.number(),
    }),
  ),
})

export const PolicyMessageSchema = v.variant('type', [
  v.object({
    type: v.literal('ask'),
    fieldId: text,
    text,
    /** The role whose queue the question blocks; defaults to scoping. */
    to: v.optional(v.picklist(POLICY_ROLES)),
    /** Who it is asked of: a person (an email) or `team:<role>`; defaults to
     *  `to`'s team. The mention index matches these, so a person asked by name
     *  finds it in their "Back with you" questions (S-N22). */
    mentions: v.optional(v.array(v.pipe(text, v.toLowerCase()))),
  }),
  v.object({
    type: v.literal('answer'),
    id: v.string(),
    text,
    /** The field's basis for a judgment-call answer: a citation, a written reason, or both. */
    basis: v.optional(
      v.object({
        citationId: v.optional(v.string()),
        rationale: v.optional(note),
      }),
    ),
  }),
  v.object({
    type: v.literal('confirm-proposal'),
    fieldId: text,
  }),
  v.object({
    type: v.literal('close-question'),
    id: v.string(),
  }),
  v.object({
    type: v.literal('reassign'),
    role: v.picklist(POLICY_ROLES),
    owner: v.nullable(text),
  }),
  v.object({
    type: v.literal('accept-suggestion'),
    id: v.string(),
  }),
  v.object({
    type: v.literal('decline-suggestion'),
    id: v.string(),
    feedback: v.optional(note),
  }),
  v.object({
    type: v.literal('undo-field'),
    fieldId: text,
  }),
  v.object({
    type: v.literal('redo-field'),
    fieldId: text,
  }),
  v.object({
    type: v.literal('restore-field'),
    fieldId: text,
    versionId: v.string(),
  }),
  v.object({
    type: v.literal('add-note'),
    fieldId: text,
    body: noteBody,
  }),
  v.object({
    type: v.literal('edit-note'),
    id: v.string(),
    body: noteBody,
  }),
  v.object({
    type: v.literal('remove-note'),
    id: v.string(),
  }),
  v.object({
    type: v.literal('save-tree-version'),
    name: v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(120)),
    description: v.optional(note),
  }),
  v.object({
    type: v.literal('restore-tree-version'),
    versionId: v.string(),
  }),
  v.object({
    type: v.literal('approve'),
    role: v.picklist(POLICY_ROLES),
    /** Compare-and-set: refuses if the definition changed under the reviewer. */
    expectedFingerprint: v.string(),
    note: v.optional(note),
    evidenceUrl: v.optional(v.pipe(v.string(), v.trim())),
  }),
  v.object({
    type: v.literal('challenge'),
    to: v.picklist(['scoping', 'design']),
    expectedFingerprint: v.string(),
    explanation: noteBody,
    evidenceUrl: v.optional(v.pipe(v.string(), v.trim())),
  }),
  v.object({
    type: v.literal('move-back'),
    explanation: noteBody,
    to: v.optional(v.picklist(['scoping', 'design', 'validation'])),
  }),
  v.object({
    type: v.literal('respond'),
    explanation: noteBody,
  }),
  v.object({
    type: v.literal('approve-record'),
    expectedFingerprint: v.string(),
    record: v.object({
      changeSummary: noteBody,
      changeType: v.picklist(CHANGE_TYPES),
      linesOfBusiness: v.array(text),
    }),
  }),
  v.object({
    type: v.literal('publish'),
  }),
  v.object({
    type: v.literal('attest-claims'),
    expectedFingerprint: v.string(),
    evidenceUrl: v.pipe(v.string(), v.trim()),
  }),
  v.object({
    type: v.literal('publish-now'),
    expectedFingerprint: v.string(),
    evidenceUrl: v.optional(v.pipe(v.string(), v.trim())),
    record: v.object({
      changeSummary: noteBody,
      changeType: v.picklist(CHANGE_TYPES),
      linesOfBusiness: v.array(text),
    }),
  }),
  v.object({
    type: v.literal('reopen'),
    note: v.optional(note),
    /** `design` reports a defect straight to design (D-N12); default scoping. */
    to: v.optional(v.picklist(['scoping', 'design'])),
  }),
  v.object({
    type: v.literal('withdraw'),
    note: v.optional(note),
  }),
  v.object({
    type: v.literal('steer-node'),
    /** Omitted: feedback on the whole tree (D6). */
    nodeId: v.optional(text),
    feedback: noteBody,
  }),
  v.object({
    type: v.literal('sync-jdm'),
  }),
  v.object({
    type: v.literal('generate-claims'),
  }),
  v.object({
    type: v.literal('judge-claim'),
    name: text,
    verdict: v.picklist(['agree', 'disagree']),
    cause: v.optional(
      v.picklist(['unclear-intent', 'bad-claim', 'wrong-logic']),
    ),
    note: v.optional(note),
    question: v.optional(noteBody),
  }),
  v.object({
    type: v.literal('mark-unreachable'),
    pathKey: text,
    reason: v.nullable(noteBody),
  }),
])
export type PolicyMessage = v.InferOutput<typeof PolicyMessageSchema>

/** Shared cursor/limit paging for the `activity` and `fieldHistory` read callables. */
const pagingQuery = {
  cursor: v.optional(v.string()),
  limit: v.optional(
    v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(200)),
  ),
}

/** Query for the `activity` read callable: the audit trail, paged, optionally searched. */
export const ActivityQuerySchema = v.object({
  ...pagingQuery,
  q: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(200))),
})

/** Query for the `fieldHistory` read callable: one row's version history, paged. */
export const FieldHistoryQuerySchema = v.object({
  fieldId: text,
  ...pagingQuery,
})

export const CreatePolicySchema = v.object({
  /** Triage's `candidate:<source>:<candidate>`; opening an existing policy is idempotent by its id instead. */
  idempotencyKey: text,
  /** Required for triage; an opened Content Library policy takes its PaAPI name. */
  title: v.optional(
    v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(200)),
  ),
  /** An existing Content Library policy to open; absent, triage creates one. */
  paapiPolicyId: v.optional(text),
  sourceId: v.optional(text),
  /** PaAPI reference document id, when the source has one resolved. */
  referenceDocumentId: v.optional(text),
  candidateId: v.optional(text),
  initiativeId: v.optional(text),
  /** Chosen in triage; seeded as `triage` proposals for scoping to confirm. */
  topic: v.optional(text),
  logicTypes: v.optional(v.array(v.picklist(LOGIC_OPTIONS))),
  /** NURO's rationale for the candidate, seeded as the policy intent proposal. */
  intent: v.optional(text),
  /** Who wrote `intent`: `triage` when an analyst typed it on a hand-added candidate. */
  intentBy: v.optional(v.picklist(['neuro', 'triage'])),
  /** The source's committed clients and date, seeded as `committed-client`/`committed-date` proposals. */
  clients: v.optional(v.array(text)),
  committedDate: v.optional(text),
  excerpt: v.optional(
    v.object({
      excerptId: v.nullable(v.string()),
      cite: v.string(),
      text: v.string(),
      anchor: v.optional(v.nullable(pdfAnchorSchema)),
    }),
  ),
  /** Pending answers the room seeds as `NURO` suggestions, never as answers. */
  suggestions: v.optional(
    v.array(
      v.object({
        fieldId: text,
        value: answerValue,
        rationale: note,
        confidence: v.optional(
          v.nullable(v.pipe(v.number(), v.minValue(0), v.maxValue(1))),
        ),
      }),
    ),
  ),
})
export type CreatePolicyInput = v.InferOutput<typeof CreatePolicySchema>

/** Where one policy's version-pinned review stands, for the registry's queues. */
export interface PolicyQueueDetail {
  version: number
  changedSinceVersion: boolean
  approvals: Record<PolicyRole, 'current' | 'stale' | null>
  lastDecision: Pick<
    PolicyDecision,
    'decision' | 'origin' | 'destination' | 'actor' | 'at' | 'note'
  > | null
  sendBack: {
    to: 'scoping' | 'design'
    explanation: string
    evidenceUrl: string | null
    by: string
    at: string
    response: string | null
  } | null
  returning: 'changed' | 'responded' | null
  /** Who sent it back, while it is `returning`: it goes back to them (V4, V-N1). */
  returnTo: string | null
  designer: string | null
  designApprovedAt: string | null
  clients: string[]
  committedDate: string | null
  workType: 'new' | 'update'
  /** Open questions with no answer, addressed to scoping. */
  unanswered: number
  claims: { covered: number; total: number; stale: boolean } | null
}

/** `PolicyAgent.assign`'s refusal for an id with no policy — matched by
 *  message, since DO RPC drops an error's class. */
export const NO_SUCH_POLICY = 'No such policy'

/** What the registry holds per policy — enough for every queue and tile. */
export interface PolicySummary {
  id: string
  title: string
  sourceId: string | null
  initiativeId: string | null
  stage: PolicyStage
  stageChangedAt: string
  owners: Record<PolicyRole, string | null>
  contractAnswered: number
  contractTotal: number
  openQuestions: number
  /** ISO time the oldest open question was asked; `null` when none is open. */
  waitingSince: string | null
  /** Who asked the oldest open question; `null` when none is open. */
  askedBy: string | null
  insights: number
  nodes: number
  publishedAt: string | null
  sourceChanged: boolean
  updatedAt: string
  queue: PolicyQueueDetail
}

/** Query for the registry's `GET /policies`, with keyset paging. */
export const ListQuerySchema = v.object({
  stage: v.optional(v.array(stage)),
  owner: v.optional(v.string()),
  /** Narrows `owner` to one role's slot; without it, any slot matches. */
  role: v.optional(v.picklist(POLICY_ROLES)),
  sourceId: v.optional(v.string()),
  initiativeId: v.optional(v.string()),
  q: v.optional(v.string()),
  sort: v.optional(v.picklist(['updated', 'stage', 'title'])),
  cursor: v.optional(v.pipe(v.string(), v.regex(/\|[^|]+$/))),
  limit: v.optional(
    v.pipe(
      v.string(),
      v.transform(Number),
      v.integer(),
      v.minValue(1),
      v.maxValue(200),
    ),
  ),
})

/** Server-side filters for the registry's `GET /policies`. */
export type PolicyListFilters = v.InferOutput<typeof ListQuerySchema>

export interface PolicyPage {
  rows: PolicySummary[]
  nextCursor: string | null
  total: number
}

/** One unresolved comment thread that tags someone, as the registry indexes it for Back with you. */
export interface MentionThread {
  /** The thread's root comment id. */
  id: string
  policyId: string
  /** What it is on: a contract field id, a `node:` or `activity:` item, or `POLICY_ITEM`. */
  itemId: string
  /** That item's name, as the policy's activity trail names it. */
  itemName: string
  /** Every tag across the root and its replies: lower-cased emails and `team:<role>`. */
  mentions: string[]
  /** Who wrote the thread's latest comment. */
  author: string
  /** The latest comment's first 140 characters. */
  excerpt: string
  /** The latest comment's time, epoch ms. */
  lastAt: number
}

/** A mention thread joined to its policy's registry row. */
export interface MentionThreadRow extends MentionThread {
  title: string
  stage: PolicyStage
}

/** Query for the registry's `GET /mentions`: the tags to match, as repeated `tag` params. */
export const MentionQuerySchema = v.object({
  tag: v.pipe(
    v.array(
      v.pipe(
        v.string(),
        v.trim(),
        v.toLowerCase(),
        v.minLength(1),
        v.maxLength(320),
      ),
    ),
    v.minLength(1),
    v.maxLength(10),
  ),
})

export interface PolicyCounts {
  byStage: Record<PolicyStage, number>
  /** Active policies whose owner slot for their current stage is empty. */
  unowned: number
  sourceChanged: number
  /** Design-stage rows with at least one question still waiting on scoping. */
  waitingOnScoping: number
}

/** A policy is called whatever scoping named it, else what it was created as. */
export const policyTitle = (view: PolicyDocView): string => {
  const name = view.answers['policy-name']
  return (typeof name === 'string' && name.trim()) || view.meta.title
}

const asStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []

/** Derives one policy's queue detail from its decisions, versions and claims. */
const queueDetail = (view: PolicyDocView): PolicyQueueDetail => {
  const latestVersion = view.versions.at(-1) ?? null
  const fingerprint = definitionFingerprint(view)
  const roleApprovals = approvals(view.decisions, view)
  const latestDecision =
    view.decisions.length > 0
      ? view.decisions.reduce((a, b) => (b.seq > a.seq ? b : a))
      : null
  const challenge = latestChallenge(view.decisions)
  const returningAs = returning(view.decisions)
  const lastChallenge = view.decisions
    .filter((d) => d.decision === 'challenged')
    .reduce<PolicyDecision | null>((a, b) => (a && a.seq > b.seq ? a : b), null)
  const respondedChallenge =
    latestDecision?.decision === 'responded'
      ? (view.decisions.find(
          (d) => d.decision === 'challenged' && d.id === latestDecision.answers,
        ) ?? null)
      : null
  const designApprovals = view.decisions.filter(
    (d) => d.decision === 'approved' && d.origin === 'design',
  )
  const latestDesignApproval = designApprovals.length
    ? designApprovals.reduce((a, b) => (b.seq > a.seq ? b : a))
    : null
  return {
    version: latestVersion?.seq ?? 0,
    changedSinceVersion: latestVersion
      ? fingerprint !== latestVersion.fingerprint
      : true,
    approvals: {
      scoping: roleApprovals.scoping.decision
        ? roleApprovals.scoping.current
          ? 'current'
          : 'stale'
        : null,
      design: roleApprovals.design.decision
        ? roleApprovals.design.current
          ? 'current'
          : 'stale'
        : null,
      validation: roleApprovals.validation.decision
        ? roleApprovals.validation.current
          ? 'current'
          : 'stale'
        : null,
    },
    lastDecision: latestDecision
      ? {
          decision: latestDecision.decision,
          origin: latestDecision.origin,
          destination: latestDecision.destination,
          actor: latestDecision.actor,
          at: latestDecision.at,
          note: latestDecision.note,
        }
      : null,
    sendBack:
      challenge &&
      (challenge.destination === 'scoping' ||
        challenge.destination === 'design')
        ? {
            to: challenge.destination,
            explanation: challenge.note ?? '',
            evidenceUrl: challenge.evidenceUrl,
            by: challenge.actor,
            at: challenge.at,
            response: null,
          }
        : respondedChallenge &&
            (respondedChallenge.destination === 'scoping' ||
              respondedChallenge.destination === 'design')
          ? {
              to: respondedChallenge.destination,
              explanation: respondedChallenge.note ?? '',
              evidenceUrl: respondedChallenge.evidenceUrl,
              by: respondedChallenge.actor,
              at: respondedChallenge.at,
              response: latestDecision!.note,
            }
          : null,
    returning: returningAs,
    returnTo: returningAs ? (lastChallenge?.actor ?? null) : null,
    designer: latestDesignApproval?.actor ?? null,
    designApprovedAt: latestDesignApproval?.at ?? null,
    clients: asStringList(view.answers['committed-client']),
    committedDate:
      typeof view.answers['committed-date'] === 'string'
        ? view.answers['committed-date']
        : null,
    workType: workType(view.decisions, view.meta),
    unanswered: view.questions.filter(
      (q) => q.answer === null && q.to === 'scoping',
    ).length,
    claims: view.claims
      ? {
          covered: coverage(view).covered,
          total: coverage(view).total,
          stale:
            view.claims.nucleusVersion !== view.jdm.version ||
            view.jdm.status !== 'synced',
        }
      : null,
  }
}

/**
 * Derives the registry row from a document read.
 *
 * `contractTotal` counts the worksheet rows that apply to this policy and
 * were not moved to "not asked" — the same denominator the scoping page
 * shows; `contractAnswered` is those `unsettledReason` calls settled, the bar
 * the scope gate applies. The title follows the policy-name answer once
 * scoping edits it.
 */
export const summarizePolicy = (
  view: PolicyDocView,
  updatedAt: string,
): PolicySummary => {
  const counted = CONTRACT_FIELDS.filter(
    (field) => onWorksheet(field, view) && !(field.id in view.notAsked),
  )
  const answered = counted.filter(
    (field) => unsettledReason(view, field) === null,
  )
  const open = view.questions
    .filter((question) => question.closedAt === null)
    .toSorted((a, b) => a.askedAt.localeCompare(b.askedAt))
  const unanswered = view.questions
    .filter((question) => question.answer === null)
    .toSorted((a, b) => a.askedAt.localeCompare(b.askedAt))
  const oldestUnanswered = unanswered[0]
  return {
    id: view.meta.id,
    title: policyTitle(view),
    sourceId: view.meta.sourceId,
    initiativeId: view.meta.initiativeId,
    stage: view.meta.stage,
    stageChangedAt: view.meta.stageChangedAt,
    owners: view.meta.owners,
    contractAnswered: answered.length,
    contractTotal: counted.length,
    openQuestions: open.length,
    waitingSince: oldestUnanswered?.askedAt ?? null,
    askedBy: oldestUnanswered?.askedBy ?? null,
    insights: view.insights.length,
    nodes: view.nodes.length,
    publishedAt: view.meta.publishedAt,
    sourceChanged: view.meta.sourceChanged,
    updatedAt,
    queue: queueDetail(view),
  }
}
